import React from "react";
import { z } from "zod";
import { Input } from "src/components/ui/Input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/Select";
import { emailSchema, phoneNumberSchema, nameSchema } from "src/services/errorHandler";
import { educationLevels } from "src/constants/educationLevels";
import { usePhoneCountry } from "src/hooks/usePhoneCountry";
// Form data interface
export interface StudentParentFormData {
  parentName: string;
  studentName: string;
  parentPhone: string;
  email: string;
  grade?: string;
  primaryGoals?: string[];
  isoCountryCode?: string;
  phoneIsdCode?: string;
}

// Props interface
export interface StudentParentFormProps {
  formData: StudentParentFormData;
  formErrors: Record<string, string>;
  onInputChange: (field: string, value: string) => void;
  onCheckboxChange?: (field: string, value: string, checked: boolean) => void;
  showGradeSelection?: boolean;
  showPrimaryGoals?: boolean;
  className?: string;
}

// Validation schema
export const studentParentFormSchema = z.object({
  parentName: nameSchema,
  studentName: nameSchema,
  parentPhone: phoneNumberSchema,
  email: emailSchema,
  grade: z.string().min(1, { message: "Please select a grade" }).optional(),
  primaryGoals: z.array(z.string()).min(1, { message: "Please select at least one goal" }).optional(),
});

// Primary goals options
const PRIMARY_GOALS_OPTIONS = [
  "Improving school grades",
  "Get help with school subjects",
  "Filling learning gaps",
  "Building strong concepts",
  "Building confidence"
];

// Grade options
const GRADE_OPTIONS = educationLevels.map((level) => ({
  value: level.id,
  label: level.label,
}));

const StudentParentForm: React.FC<StudentParentFormProps> = ({
  formData,
  formErrors,
  onInputChange,
  onCheckboxChange,
  showGradeSelection = false,
  showPrimaryGoals = false,
  className = "",
}) => {
  const { countries, selectedCountryCode, setSelectedCountryCode, selected, isdPrefix } = usePhoneCountry();

  // Ensure phone country metadata is propagated to parent on mount and when selection changes
  React.useLayoutEffect(() => {
    if (selectedCountryCode) {
      onInputChange("isoCountryCode", selectedCountryCode);
    }
    if (isdPrefix) {
      onInputChange("phoneIsdCode", String(isdPrefix));
    }
  }, [selectedCountryCode, isdPrefix]);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Name fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Parent's Name <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Enter parent's name"
            value={formData.parentName}
            onChange={(e) => onInputChange("parentName", e.target.value)}
            className={formErrors.parentName ? "border-red-500" : ""}
          />
          {formErrors.parentName && (
            <p className="text-red-500 text-xs mt-1">{formErrors.parentName}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Student's Name <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Enter student's name"
            value={formData.studentName}
            onChange={(e) => onInputChange("studentName", e.target.value)}
            className={formErrors.studentName ? "border-red-500" : ""}
          />
          {formErrors.studentName && (
            <p className="text-red-500 text-xs mt-1">{formErrors.studentName}</p>
          )}
        </div>
      </div>

      {/* Contact fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Parent's Phone Number <span className="text-red-500">*</span>
          </label>
          <div className="flex">
            {/* Country dropdown */}
            <Select
              value={selectedCountryCode}
              onValueChange={(val) => {
                setSelectedCountryCode(val);
                // Propagate selected country code and ISD prefix to parent
                try {
                  onInputChange("isoCountryCode", val);
                  const country = countries.find((c) => c.code === val);
                  const isd = country ? `+${country.isdCode}` : (isdPrefix || "");
                  onInputChange("phoneIsdCode", isd);
                } catch {}
              }}
            >
              <SelectTrigger className="rounded-r-none w-24 h-10 px-2 border-r-0">
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  {selected && (
                    <img src={selected.flag} alt={selected.code} className="w-4 h-4 rounded-sm flex-shrink-0" />
                  )}
                  <span className="text-sm flex-shrink-0">{isdPrefix}</span>
                </div>
              </SelectTrigger>
              <SelectContent className="max-h-72 overflow-auto">
                {countries.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    <div className="flex items-center gap-2 w-full">
                      <img src={c.flag} alt={c.code} className="w-4 h-4 rounded-sm flex-shrink-0" />
                      <span className="flex-1">{c.name}</span>
                      <span className="text-gray-500 text-sm">+{c.isdCode}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Phone input */}
            <Input
              placeholder="Enter your phone number"
              value={formData.parentPhone}
              onChange={(e) => onInputChange("parentPhone", e.target.value)}
              className={`rounded-l-none h-10 flex-1 ${formErrors.parentPhone ? "border-red-500" : ""}`}
            />
          </div>
          {formErrors.parentPhone && (
            <p className="text-red-500 text-xs mt-1">{formErrors.parentPhone}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Enter your email address"
            type="email"
            value={formData.email}
            onChange={(e) => onInputChange("email", e.target.value)}
            className={formErrors.email ? "border-red-500" : ""}
          />
          {formErrors.email && (
            <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
          )}
        </div>
      </div>

      {/* Grade selection - conditional */}
      {showGradeSelection && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Select Grade <span className="text-red-500">*</span>
          </label>
          <Select value={formData.grade || ""} onValueChange={(value) => onInputChange("grade", value)}>
            <SelectTrigger className={formErrors.grade ? "border-red-500" : ""}>
              <SelectValue placeholder="Select grade" />
            </SelectTrigger>
            <SelectContent>
              {GRADE_OPTIONS.map((grade) => (
                <SelectItem key={grade.value} value={grade.value}>
                  {grade.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {formErrors.grade && (
            <p className="text-red-500 text-xs mt-1">{formErrors.grade}</p>
          )}
        </div>
      )}

      {/* Primary goals - conditional */}
      {showPrimaryGoals && onCheckboxChange && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            WHAT ARE YOUR PRIMARY GOALS? <span className="text-red-500">*</span>
          </label>
          <div className="space-y-2">
            {PRIMARY_GOALS_OPTIONS.map((goal) => (
              <label key={goal} className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  checked={formData.primaryGoals?.includes(goal) || false}
                  onChange={(e) => onCheckboxChange("primaryGoals", goal, e.target.checked)}
                />
                <span className="ml-2 text-sm text-gray-700">{goal}</span>
              </label>
            ))}
          </div>
          {formErrors.primaryGoals && (
            <p className="text-red-500 text-xs mt-1">{formErrors.primaryGoals}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentParentForm;